import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '../utils.ts';

interface CardProps extends ComponentPropsWithoutRef<'div'> {
  /** `default` = soft-shadowed white card; `inset` = subtle background with no shadow. */
  variant?: 'default' | 'inset' | 'dark';
  /** `md` = p-6; `lg` = p-7 (matches marketing); `sm` = p-4 for table-adjacent cards. */
  padding?: 'sm' | 'md' | 'lg' | 'none';
  /** Apply hover lift (defaults to off; opt-in for clickable cards). */
  interactive?: boolean;
  children: ReactNode;
}

const variants = {
  default: 'bg-surface border border-light-silver shadow-soft',
  inset: 'bg-light-silver/40 border border-light-silver/60',
  dark: 'bg-ink text-white border border-white/[0.06] shadow-[0_30px_80px_-30px_rgba(2,11,29,0.65)] dark:bg-gradient-premium-dark dark:border-white/10',
} satisfies Record<string, string>;

const paddings = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-7',
} satisfies Record<string, string>;

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'default', padding = 'md', interactive = false, className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-card',
        variants[variant],
        paddings[padding],
        interactive &&
          'transition-shadow duration-fast ease-clickt hover:shadow-hover focus-within:shadow-hover cursor-pointer',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});

export function CardHeader({
  title,
  description,
  action,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <h3 className="text-display-4 font-semibold text-midnight font-display leading-tight">
          {title}
        </h3>
        {description ? (
          <p className="mt-1.5 text-body-md text-cool-gray leading-relaxed">{description}</p>
        ) : null}
      </div>
      {action ? <div className="flex-none">{action}</div> : null}
    </div>
  );
}
